/**
 * WealthDistribution model (B12) — pure helpers behind the wealth-by-asset-class
 * donut. Kept in its own module (like dashboard-letterhead.ts) so the component
 * file only exports a component and the grouping logic stays unit-testable.
 */

import type { AssetType, AssetView } from '../../lib/api';
import { assetTypeLabel } from '../../lib/api';

/** Design-token colour per §16 asset type (donut slices + legend swatches). */
export const TYPE_COLOR: Record<AssetType, string> = {
  property: 'var(--primary-700)',
  investment: 'var(--primary-400)',
  'company-shares': 'var(--accent-600)',
  vehicle: 'var(--accent-300)',
  other: 'var(--ink-3)',
};

export interface WealthSlice {
  type: AssetType;
  label: string;
  /** Summed value for this class, in CENTS. */
  valueCents: number;
  color: string;
}

/**
 * Group assets by type, sum value (cents), drop zero/negative-value classes,
 * and order largest-first. PURE — unit-tested directly (B12 empty-vs-populated).
 */
export function buildWealthSlices(assets: AssetView[]): WealthSlice[] {
  const byType = new Map<AssetType, number>();
  for (const a of assets) {
    if (!a.valueZar || a.valueZar <= 0) continue;
    byType.set(a.assetType, (byType.get(a.assetType) ?? 0) + a.valueZar);
  }
  const slices: WealthSlice[] = [];
  for (const [type, valueCents] of byType) {
    slices.push({ type, label: assetTypeLabel(type), valueCents, color: TYPE_COLOR[type] });
  }
  return slices.sort((a, b) => b.valueCents - a.valueCents);
}
